// Which product ("treino" vs "DI/frota") to show is decided purely by the
// hostname the browser connected to — same build-time-env pattern already
// used by App.js::getBackendUrl(). Fleet domains are fronted by a Cloudflare
// Worker (see infra/cloudflare-worker/fleet-proxy.js) that rewrites Host
// before forwarding to this same deployment, so this is the only signal
// available client-side.
const FLEET_DOMAINS = (process.env.REACT_APP_FLEET_DOMAINS || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

export const isFleetDomain = () =>
  typeof window !== "undefined" && FLEET_DOMAINS.includes(window.location.hostname);

// Only-fleet experience: the fleet domain, or a school whose product is fleet. In both cases no
// training screen exists (dashboards, exams, ranking, badges...). The server already refuses the
// training APIs for fleet schools; the domain is only visible to the browser, so it is checked here.
export const isFleetOnly = (school) => isFleetDomain() || school?.product === "fleet";

// Where a pilot or instructor lands in a fleet-only experience: the reservations page.
export const FLEET_HOME = "/reservas";

const FLEET_TITLE = "Aerofleet Center";
// Blue accent matching the Aerofleet app icon (#1673f9 = 500).
const FLEET_ACCENT = {
  50: "238 245 255", 100: "220 233 255", 200: "183 211 254", 300: "134 181 253", 400: "79 147 251",
  500: "22 115 249", 600: "18 89 214", 700: "14 71 174", 800: "15 58 138", 900: "16 48 107", 950: "10 28 64",
};
const FLEET_DESCRIPTION =
  "Gestão de frota, manutenções, licenças e conformidade para escolas e operadores.";

// index.html is static and carries the training branding; on a fleet domain
// replace everything visible to the user (tab title, link previews read after
// JS runs, installed-app name) before the first render.
export function applyFleetBranding() {
  if (!isFleetDomain()) return;
  const setMeta = (selector, value) => {
    const el = document.querySelector(selector);
    if (el) el.setAttribute("content", value);
  };
  document.documentElement.setAttribute("data-product", "fleet");
  document.title = FLEET_TITLE;
  setMeta('meta[name="description"]', FLEET_DESCRIPTION);
  setMeta('meta[property="og:site_name"]', FLEET_TITLE);
  setMeta('meta[property="og:title"]', FLEET_TITLE);
  setMeta('meta[property="og:description"]', FLEET_DESCRIPTION);
  setMeta('meta[name="twitter:title"]', FLEET_TITLE);
  setMeta('meta[name="twitter:description"]', FLEET_DESCRIPTION);
  setMeta('meta[name="apple-mobile-web-app-title"]', FLEET_TITLE);
  document.querySelector('link[rel="manifest"]')?.setAttribute("href", "/manifest-fleet.json");
  // Own app icon (blue "AFC"): the training app uses the orange "UTC" one, so the two must not look alike
  // on a phone's home screen.
  document.querySelectorAll('link[rel="apple-touch-icon"], link[rel="icon"]').forEach((el) => {
    const href = el.getAttribute("href") || "";
    if (href.startsWith("/icons/icon-")) el.setAttribute("href", href.replace("/icons/icon-", "/icons/fleet-icon-"));
  });
  setMeta('meta[name="msapplication-TileImage"]', "/icons/fleet-icon-192.png");
  setMeta('meta[name="theme-color"]', "#1673f9");
  // Accent palette: the app's orange utilities read these variables (tailwind.config.js), so on this domain
  // every accent, button and glow becomes the icon blue. Space-separated RGB.
  Object.entries(FLEET_ACCENT).forEach(([shade, rgb]) => document.documentElement.style.setProperty(`--orange-${shade}`, rgb));
  document.querySelector('link[rel="canonical"]')?.remove();
  document.querySelector('link[rel="llms"]')?.remove();
  document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => el.remove());
}
